import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Download, AlertCircle, CheckCircle2, Calendar } from 'lucide-react'
import { api } from '@/lib/api/client'
import { BrandGlyph } from './IntegrationsPage'
import type { CalendarificStatus, CalendarificImportResult, PublicHoliday } from '@/types/api'

const GLYPH = {
  monogram: 'CL',
  gradient: 'linear-gradient(135deg, oklch(0.68 0.17 45) 0%, oklch(0.55 0.18 38) 100%)',
}

const CURRENT_YEAR = new Date().getFullYear()
const YEARS        = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2]

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Calendarific</span>
    </div>
  )
}

export default function CalendarificPage() {
  const queryClient = useQueryClient()
  const [year,        setYear]        = useState(CURRENT_YEAR)
  const [countryCode, setCountryCode] = useState('SE')
  const [lastResult,  setLastResult]  = useState<CalendarificImportResult | null>(null)

  const { data: status, isLoading: statusLoading, isError: statusError, error: statusQueryError } = useQuery({
    queryKey: ['calendarific', 'status'],
    queryFn:  () => api.get<CalendarificStatus>('/settings/calendarific/status'),
  })

  const { data: holidays = [], isFetching: holidaysFetching } = useQuery({
    queryKey: ['admin', 'public-holidays', year, countryCode],
    queryFn:  () => api.get<PublicHoliday[]>(`/settings/public-holidays?year=${year}&countryCode=${countryCode}`),
    staleTime: 60 * 60 * 1000,
  })

  const importMutation = useMutation({
    mutationFn: () => api.post<CalendarificImportResult>('/settings/calendarific/import', { year, countryCode }),
    onSuccess: (result) => {
      setLastResult(result)
      queryClient.invalidateQueries({ queryKey: ['admin', 'public-holidays'] })
      queryClient.invalidateQueries({ queryKey: ['time', 'my-available-hours'] })
    },
  })

  if (statusLoading) {
    return (
      <div className="page">
        <Breadcrumb />
        <p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p>
      </div>
    )
  }

  if (statusError) {
    return (
      <div className="page">
        <Breadcrumb />
        <div className="card" style={{ padding: 16, marginTop: 20, border: '1px solid transparent', background: 'var(--ns-err-soft)' }}>
          <div className="row" style={{ gap: 10 }}>
            <AlertCircle size={16} style={{ color: 'var(--ns-err)', flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Could not reach the backend</div>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                {statusQueryError instanceof Error ? statusQueryError.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero header */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, oklch(0.68 0.17 45 / 0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Calendarific</h1>
              {status?.configured
                ? <span className="badge badge-ok"><span className="dot dot-ok" />Configured</span>
                : <span className="badge badge-warn"><AlertCircle size={11} />Not configured</span>
              }
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Import public holidays for any country and year directly from the Calendarific API.
            </p>
            <div className="row" style={{ gap: 12, marginTop: 12, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
              <span className="row" style={{ gap: 5 }}><Calendar size={12} />calendarific.com</span>
              <span style={{ width: 1, height: 12, background: 'var(--ns-border)' }} />
              <span>On-demand import</span>
            </div>
          </div>
        </div>
      </div>

      {/* API key not configured notice */}
      {!status?.configured && (
        <div className="card" style={{ padding: 16, marginBottom: 20, border: '1px solid var(--ns-warn-border)', background: 'var(--ns-warn-soft)' }}>
          <div className="row" style={{ gap: 10 }}>
            <AlertCircle size={16} style={{ color: 'var(--ns-warn)', flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>API key not configured</div>
              <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>
                Add <code className="mono" style={{ fontSize: 11.5, padding: '1px 5px', borderRadius: 3, background: 'var(--ns-surface-2)' }}>CALENDARIFIC_API_KEY</code> to
                the function app settings (Key Vault reference in production, <code className="mono" style={{ fontSize: 11.5, padding: '1px 5px', borderRadius: 3, background: 'var(--ns-surface-2)' }}>local.settings.json</code> locally).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Import panel */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14 }}>Import holidays</div>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Year</label>
            <select
              className="input"
              style={{ width: 100 }}
              value={year}
              onChange={e => { setYear(Number(e.target.value)); setLastResult(null) }}
            >
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Country code</label>
            <input
              className="input"
              style={{ width: 72 }}
              value={countryCode}
              maxLength={2}
              placeholder="SE"
              onChange={e => { setCountryCode(e.target.value.toUpperCase()); setLastResult(null) }}
            />
          </div>
          <button
            className="btn btn-brand"
            disabled={!status?.configured || importMutation.isPending || !countryCode}
            onClick={() => importMutation.mutate()}
          >
            <Download size={14} />
            {importMutation.isPending ? 'Importing…' : 'Import holidays'}
          </button>
        </div>

        {/* Result banner */}
        {lastResult && !importMutation.isPending && (
          <div style={{
            marginTop: 14, padding: '10px 14px', borderRadius: 6,
            background: lastResult.imported > 0 ? 'var(--ns-ok-soft)' : 'var(--ns-surface-2)',
            border: `1px solid ${lastResult.imported > 0 ? 'var(--ns-ok-border)' : 'var(--ns-border)'}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <CheckCircle2 size={15} style={{ color: lastResult.imported > 0 ? 'var(--ns-ok)' : 'var(--ns-text-muted)', flexShrink: 0 }} />
            <span style={{ fontSize: 13 }}>
              <strong className="tabular">{lastResult.imported}</strong> holidays imported,{' '}
              <strong className="tabular">{lastResult.skipped}</strong> already existed
              {lastResult.errors > 0 && <>, <span style={{ color: 'var(--ns-warn)' }}>{lastResult.errors} errors</span></>}
              {' '}— {lastResult.year} / {lastResult.countryCode}
            </span>
          </div>
        )}

        {importMutation.isError && (
          <p style={{ marginTop: 10, fontSize: 12.5, color: 'var(--ns-err)' }}>
            Import failed — check the API key and try again.
          </p>
        )}
      </div>

      {/* Holidays table */}
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
        Holidays in database — {year} / {countryCode || '…'}
      </div>
      {holidaysFetching ? (
        <p className="muted" style={{ textAlign: 'center', padding: 24 }}>Loading…</p>
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th style={{ width: 80 }}>Country</th>
              </tr>
            </thead>
            <tbody>
              {holidays.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', color: 'var(--ns-text-muted)', padding: 24 }}>
                    No holidays in database for {year} / {countryCode || '…'}.
                    {status?.configured && ' Use the import button above to fetch them.'}
                  </td>
                </tr>
              ) : holidays.map(h => (
                <tr key={h.id}>
                  <td className="mono tabular">{h.date}</td>
                  <td>{h.name}</td>
                  <td><span className="badge">{h.countryCode}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <Link to="/settings/public-holidays" style={{ fontSize: 12.5, color: 'var(--ns-brand-strong)' }}>
          Manage holidays manually →
        </Link>
      </div>
    </div>
  )
}
